import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { ChevronLeft, Search, Edit2, Trash2, PlusCircle, X, Save, User, Mail, BookOpen, GraduationCap, MessageSquare, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Student, Class, StudentWithClass, Appreciation } from '../../types/supabase';
import { useAuth } from '../../hooks/useAuth';

interface AppreciationFormData {
  content: string;
  start_date: string;
  end_date: string;
  subject_id?: string;
}

const AdminStudents = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<StudentWithClass[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAppreciationModal, setShowAppreciationModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStudent, setEditingStudent] = useState<StudentWithClass | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<StudentWithClass | null>(null);
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    email: '',
    class_id: ''
  });
  const [appreciationForm, setAppreciationForm] = useState<AppreciationFormData>({
    content: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    subject_id: undefined
  });
  const [subjects, setSubjects] = useState<any[]>([]);
  const [appreciations, setAppreciations] = useState<Appreciation[]>([]);

  // Admin livret fields modal (Sprint 2)
  const [showLivretAdminModal, setShowLivretAdminModal] = useState(false);
  const [livretAdminStudent, setLivretAdminStudent] = useState<StudentWithClass | null>(null);
  const [livretAdminForm, setLivretAdminForm] = useState<{
    numero_candidat: string;
    session_examen: string;
    langue_vivante_1: string;
    date_naissance: string;
    lieu_naissance: string;
    ine: string;
    sexe: 'M' | 'F' | 'X' | '';
  }>({
    numero_candidat: '',
    session_examen: '',
    langue_vivante_1: 'Anglais',
    date_naissance: '',
    lieu_naissance: '',
    ine: '',
    sexe: '',
  });
  const [livretAdminSaving, setLivretAdminSaving] = useState(false);
  const [livretAdminError, setLivretAdminError] = useState<string | null>(null);

  const openLivretAdminModal = (student: StudentWithClass) => {
    setLivretAdminStudent(student);
    const raw = student as unknown as Record<string, unknown>;
    setLivretAdminForm({
      numero_candidat: (raw.numero_candidat as string | null) ?? '',
      session_examen: raw.session_examen ? String(raw.session_examen) : '',
      langue_vivante_1: (raw.langue_vivante_1 as string | null) ?? 'Anglais',
      date_naissance: (raw.date_naissance as string | null) ?? '',
      lieu_naissance: (raw.lieu_naissance as string | null) ?? '',
      ine: (raw.ine as string | null) ?? '',
      sexe: ((raw.sexe as 'M' | 'F' | 'X' | null) ?? '') as 'M' | 'F' | 'X' | '',
    });
    setLivretAdminError(null);
    setShowLivretAdminModal(true);
  };

  const saveLivretAdmin = async (e: FormEvent) => {
    e.preventDefault();
    if (!livretAdminStudent) return;

    // Validation numéro de candidat
    if (livretAdminForm.numero_candidat && !/^C\d{2,3}$/.test(livretAdminForm.numero_candidat.trim())) {
      setLivretAdminError('Format attendu : C01, C02, ... (lettre C + 2 ou 3 chiffres)');
      return;
    }
    // Validation session d'examen
    let sessionYearNum: number | null = null;
    if (livretAdminForm.session_examen) {
      sessionYearNum = Number(livretAdminForm.session_examen);
      if (Number.isNaN(sessionYearNum) || sessionYearNum < 2020 || sessionYearNum > 2050) {
        setLivretAdminError("Session d'examen invalide (2020-2050).");
        return;
      }
    }
    // Validation date de naissance
    if (livretAdminForm.date_naissance) {
      const dob = new Date(livretAdminForm.date_naissance);
      if (Number.isNaN(dob.getTime())) {
        setLivretAdminError('Date de naissance invalide.');
        return;
      }
      const yearsAgo = (Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      if (yearsAgo < 14 || yearsAgo > 100) {
        setLivretAdminError('Date de naissance irréaliste (14-100 ans attendus).');
        return;
      }
    }
    // Validation INE : 11 caractères alphanumériques
    const ineTrim = livretAdminForm.ine.trim().toUpperCase();
    if (ineTrim && !/^[A-Z0-9]{11}$/.test(ineTrim)) {
      setLivretAdminError('INE invalide : 11 caractères alphanumériques attendus.');
      return;
    }

    setLivretAdminSaving(true);
    setLivretAdminError(null);

    try {
      const payload = {
        numero_candidat: livretAdminForm.numero_candidat.trim() || null,
        session_examen: sessionYearNum,
        langue_vivante_1: livretAdminForm.langue_vivante_1.trim() || null,
        date_naissance: livretAdminForm.date_naissance || null,
        lieu_naissance: livretAdminForm.lieu_naissance.trim() || null,
        ine: ineTrim || null,
        sexe: livretAdminForm.sexe || null,
      };
      const { error } = await supabase
        .from('students')
        .update(payload)
        .eq('id', livretAdminStudent.id);
      if (error) throw error;

      // Update local list
      setStudents((prev) =>
        prev.map((s) =>
          s.id === livretAdminStudent.id
            ? ({ ...s, ...payload } as StudentWithClass)
            : s
        )
      );
      setShowLivretAdminModal(false);
    } catch (err) {
      setLivretAdminError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLivretAdminSaving(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchClasses();
    fetchSubjects();
    fetchAppreciations();
  }, []);

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .order('name');

      if (error) throw error;
      setSubjects(data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchAppreciations = async () => {
    try {
      const { data, error } = await supabase
        .from('appreciations')
        .select(`
          *,
          subject:subjects(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAppreciations(data);
    } catch (error) {
      console.error('Error fetching appreciations:', error);
    }
  };

  const handleAddAppreciation = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedStudent || !user) return;

    try {
      const { error } = await supabase
        .from('appreciations')
        .insert([{
          student_id: selectedStudent.id,
          class_id: selectedStudent.class_id,
          subject_id: appreciationForm.subject_id || null,
          author_id: user.id,
          content: appreciationForm.content,
          start_date: appreciationForm.start_date,
          end_date: appreciationForm.end_date
        }]);

      if (error) throw error;

      // Reset form and refresh data
      setAppreciationForm({
        content: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        subject_id: undefined
      });
      setShowAppreciationModal(false);
      await fetchAppreciations();
    } catch (error) {
      console.error('Error adding appreciation:', error);
      setError('Error adding appreciation');
    }
  };

  const fetchStudents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get all students with their class information
      const { data, error } = await supabase
        .from('students')
        .select(`
          *,
          class:classes(id, name, class_number, period)
        `)
        .order('last_name');

      if (error) {
        throw error;
      }

      setStudents(data as StudentWithClass[] || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Erreur lors du chargement des étudiants');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .order('name')
        .order('class_number')
        .eq('archived', false);  // Only show active classes

      if (error) {
        throw error;
      }

      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleAddStudent = async (e: FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    setError(null);

    try {
      // Check if email already exists
      const { data: existingStudents, error: checkError } = await supabase
        .from('students')
        .select('*')
        .eq('email', newStudent.email);

      if (checkError) {
        throw checkError;
      }

      if (existingStudents && existingStudents.length > 0) {
        setError('Un étudiant avec cet email existe déjà');
        setIsLoading(false);
        return;
      }

      // Add the new student with class_id
      const { data, error } = await supabase
        .from('students')
        .insert([{
          first_name: newStudent.first_name,
          last_name: newStudent.last_name,
          email: newStudent.email,
          class_id: newStudent.class_id || null
        }])
        .select(`
          *,
          class:classes(id, name, class_number, period)
        `);

      if (error) {
        throw error;
      }

      // Update the students list
      if (data && data.length > 0) {
        setStudents([...students, data[0]]);
      }
      
      setShowModal(false);
      setNewStudent({
        first_name: '',
        last_name: '',
        email: '',
        class_id: ''
      });
    } catch (error) {
      console.error('Error adding student:', error);
      setError('Erreur lors de l\'ajout de l\'étudiant');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditStudent = (student: StudentWithClass) => {
    setEditingStudent({...student});
  };

  const handleSaveEdit = async () => {
    if (!editingStudent) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if email is changed and already exists
      if (editingStudent.email !== students.find(s => s.id === editingStudent.id)?.email) {
        const { data: existingStudents, error: checkError } = await supabase
          .from('students')
          .select('*')
          .eq('email', editingStudent.email)
          .neq('id', editingStudent.id);
  
        if (checkError) {
          throw checkError;
        }
  
        if (existingStudents && existingStudents.length > 0) {
          setError('Un étudiant avec cet email existe déjà');
          setIsLoading(false);
          return;
        }
      }

      // Update the student with class_id
      const { data, error } = await supabase
        .from('students')
        .update({
          first_name: editingStudent.first_name,
          last_name: editingStudent.last_name,
          email: editingStudent.email,
          class_id: editingStudent.class_id || null
        })
        .eq('id', editingStudent.id)
        .select(`
          *,
          class:classes(id, name, class_number, period)
        `);

      if (error) {
        throw error;
      }

      // Update the students list
      if (data && data.length > 0) {
        setStudents(students.map(s => s.id === editingStudent.id ? data[0] : s));
      } else {
        // Fallback if no data returned, just update the local state
        const updatedStudents = students.map(s => {
          if (s.id === editingStudent.id) {
            // Find the class details
            const classObj = classes.find(c => c.id === editingStudent.class_id);
            return {
              ...editingStudent,
              class: classObj || null
            };
          }
          return s;
        });
        setStudents(updatedStudents);
      }
      
      setEditingStudent(null);
    } catch (error) {
      console.error('Error updating student:', error);
      setError('Erreur lors de la mise à jour de l\'étudiant');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingStudent(null);
  };

  const handleDeleteStudent = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet étudiant? Cette action supprimera également toutes ses notes.')) {
      setIsLoading(true);
      setError(null);
      
      try {
        // First delete all grades for this student
        const { error: gradesError } = await supabase
          .from('grades')
          .delete()
          .eq('student_id', id);

        if (gradesError) {
          throw gradesError;
        }

        // Then delete the student
        const { error } = await supabase
          .from('students')
          .delete()
          .eq('id', id);

        if (error) {
          throw error;
        }

        // Update the students list
        setStudents(students.filter(s => s.id !== id));
      } catch (error) {
        console.error('Error deleting student:', error);
        setError('Erreur lors de la suppression de l\'étudiant');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const filteredStudents = students.filter(student => {
    const searchString = `${student.first_name} ${student.last_name} ${student.email}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  // Format class display name
  const formatClassDisplay = (classObj: any) => {
    if (!classObj) return 'Aucune';
    return `${classObj.name} ${classObj.class_number} (${classObj.period})`;
  };

  return (
    <div className="animate-fade-in">
      <header className="flex justify-between items-center mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
            <ChevronLeft className="h-4 w-4 mr-1" />
            <span>Retour au tableau de bord</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Étudiants</h1>
          <p className="mt-2 text-gray-600">
            Ajoutez, modifiez et supprimez les étudiants du système
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary inline-flex items-center"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          Ajouter un étudiant
        </button>
      </header>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="mb-6">
        <div className="relative w-full md:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="input-field pl-10"
            placeholder="Rechercher un étudiant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th className="px-4 py-2">Nom</th>
              <th className="px-4 py-2">Prénom</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Classe</th>
              <th className="px-4 py-2">Appréciations</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-8">Chargement...</td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  {searchTerm ? 'Aucun résultat trouvé' : 'Aucun étudiant trouvé'}
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td>
                    {editingStudent?.id === student.id ? (
                      <input
                        type="text"
                        value={editingStudent.last_name}
                        onChange={(e) => setEditingStudent({ ...editingStudent, last_name: e.target.value })}
                        className="input-field py-1 px-2"
                      />
                    ) : (
                      student.last_name
                    )}
                  </td>
                  <td>
                    {editingStudent?.id === student.id ? (
                      <input
                        type="text"
                        value={editingStudent.first_name}
                        onChange={(e) => setEditingStudent({ ...editingStudent, first_name: e.target.value })}
                        className="input-field py-1 px-2"
                      />
                    ) : (
                      student.first_name
                    )}
                  </td>
                  <td>
                    {editingStudent?.id === student.id ? (
                      <input
                        type="email"
                        value={editingStudent.email}
                        onChange={(e) => setEditingStudent({ ...editingStudent, email: e.target.value })}
                        className="input-field py-1 px-2"
                      />
                    ) : (
                      student.email
                    )}
                  </td>
                  <td>
                    {editingStudent?.id === student.id ? (
                      <select
                        value={editingStudent.class_id || ''}
                        onChange={(e) => setEditingStudent({ ...editingStudent, class_id: e.target.value || null })}
                        className="input-field py-1 px-2"
                      >
                        <option value="">Aucune classe</option>
                        {classes.map((classObj) => (
                          <option key={classObj.id} value={classObj.id}>
                            {classObj.name} {classObj.class_number} ({classObj.period})
                          </option>
                        ))}
                      </select>
                    ) : (
                      formatClassDisplay(student.class)
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {appreciations.filter(a => a.student_id === student.id).length > 0 ? (
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4 text-blue-500" />
                        <span>{appreciations.filter(a => a.student_id === student.id).length}</span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditStudent(student)}
                        className="p-1 text-primary-600 hover:text-primary-800 rounded-md"
                        title="Modifier"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openLivretAdminModal(student)}
                        className="p-1 text-indigo-600 hover:text-indigo-800 rounded-md"
                        title="Champs administratifs livret (N° candidat, session, LV1)"
                      >
                        <FileText className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowAppreciationModal(true);
                        }}
                        className="p-1 text-blue-600 hover:text-blue-800 rounded-md"
                        title="Ajouter une appréciation"
                      >
                        <MessageSquare className="h-5 w-5" />
                      </button>
                      <Link 
                        to={`/admin/grades?student=${student.id}`}
                        className="p-1 text-accent-600 hover:text-accent-800 rounded-md"
                        title="Voir les notes"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-graduation-cap">
                          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                          <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/>
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleDeleteStudent(student.id)}
                        className="p-1 text-error-600 hover:text-error-800 rounded-md"
                        title="Supprimer"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Student Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4 animate-slide-up">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Ajouter un étudiant</h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddStudent}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="last_name"
                      value={newStudent.last_name}
                      onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })}
                      className="input-field pl-10"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="first_name"
                      value={newStudent.first_name}
                      onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                      className="input-field pl-10"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                      className="input-field pl-10"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="class_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Classe
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BookOpen className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="class_id"
                      value={newStudent.class_id}
                      onChange={(e) => setNewStudent({ ...newStudent, class_id: e.target.value })}
                      className="input-field pl-10"
                    >
                      <option value="">Aucune classe</option>
                      {classes.map((classObj) => (
                        <option key={classObj.id} value={classObj.id}>
                          {classObj.name} {classObj.class_number} ({classObj.period})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-outline"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Livret Fields Modal (Sprint 2) */}
      {showLivretAdminModal && livretAdminStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl mx-4 my-auto animate-slide-up">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold inline-flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  Fiche livret étudiant
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {livretAdminStudent.last_name.toUpperCase()} {livretAdminStudent.first_name}
                </p>
              </div>
              <button
                onClick={() => setShowLivretAdminModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-800">
              Tous les champs du livret sont modifiables par l'admin. L'étudiant peut également saisir
              lui-même sa date de naissance, son lieu de naissance, son INE et son sexe depuis son profil.
            </div>

            <form onSubmit={saveLivretAdmin} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de candidat
                  </label>
                  <input
                    type="text"
                    value={livretAdminForm.numero_candidat}
                    onChange={(e) =>
                      setLivretAdminForm({
                        ...livretAdminForm,
                        numero_candidat: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="C01"
                    maxLength={4}
                    className="input-field w-full font-mono uppercase"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    C01 à C40 (position dans le template).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session d'examen
                  </label>
                  <input
                    type="number"
                    value={livretAdminForm.session_examen}
                    onChange={(e) =>
                      setLivretAdminForm({ ...livretAdminForm, session_examen: e.target.value })
                    }
                    placeholder="2026"
                    min="2020"
                    max="2050"
                    className="input-field w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Langue vivante 1
                </label>
                <select
                  value={livretAdminForm.langue_vivante_1}
                  onChange={(e) =>
                    setLivretAdminForm({ ...livretAdminForm, langue_vivante_1: e.target.value })
                  }
                  className="input-field w-full"
                >
                  <option value="Anglais">Anglais</option>
                  <option value="Espagnol">Espagnol</option>
                  <option value="Allemand">Allemand</option>
                  <option value="Italien">Italien</option>
                  <option value="">— Non renseigné —</option>
                </select>
              </div>

              <div className="border-t pt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                  État civil
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date de naissance
                    </label>
                    <input
                      type="date"
                      value={livretAdminForm.date_naissance}
                      onChange={(e) =>
                        setLivretAdminForm({ ...livretAdminForm, date_naissance: e.target.value })
                      }
                      className="input-field w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sexe
                    </label>
                    <select
                      value={livretAdminForm.sexe}
                      onChange={(e) =>
                        setLivretAdminForm({
                          ...livretAdminForm,
                          sexe: e.target.value as 'M' | 'F' | 'X' | '',
                        })
                      }
                      className="input-field w-full"
                    >
                      <option value="">— Non renseigné —</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                      <option value="X">Autre / Non spécifié</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieu de naissance
                </label>
                <input
                  type="text"
                  value={livretAdminForm.lieu_naissance}
                  onChange={(e) =>
                    setLivretAdminForm({ ...livretAdminForm, lieu_naissance: e.target.value })
                  }
                  placeholder="Paris (75)"
                  maxLength={100}
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  INE (Identifiant National Étudiant)
                </label>
                <input
                  type="text"
                  value={livretAdminForm.ine}
                  onChange={(e) =>
                    setLivretAdminForm({ ...livretAdminForm, ine: e.target.value.toUpperCase() })
                  }
                  placeholder="12AB34567CD"
                  maxLength={11}
                  className="input-field w-full font-mono uppercase"
                />
                <p className="text-xs text-gray-500 mt-1">
                  11 caractères alphanumériques. Figure sur le relevé de notes du bac.
                </p>
              </div>

              {livretAdminError && (
                <div className="p-3 bg-error-50 border border-error-200 rounded text-sm text-error-700">
                  {livretAdminError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowLivretAdminModal(false)}
                  className="btn btn-outline"
                  disabled={livretAdminSaving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary inline-flex items-center gap-2"
                  disabled={livretAdminSaving}
                >
                  <Save className="h-4 w-4" />
                  {livretAdminSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appreciation Modal */}
      {showAppreciationModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Ajouter une appréciation pour {selectedStudent.first_name} {selectedStudent.last_name}
              </h3>
              <button
                onClick={() => setShowAppreciationModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleAddAppreciation}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Matière (optionnel)
                  </label>
                  <select
                    value={appreciationForm.subject_id || ''}
                    onChange={(e) => setAppreciationForm({
                      ...appreciationForm,
                      subject_id: e.target.value || undefined
                    })}
                    className="input-field"
                  >
                    <option value="">Appréciation générale</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Période du
                  </label>
                  <input
                    type="date"
                    value={appreciationForm.start_date}
                    onChange={(e) => setAppreciationForm({
                      ...appreciationForm,
                      start_date: e.target.value
                    })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    au
                  </label>
                  <input
                    type="date"
                    value={appreciationForm.end_date}
                    onChange={(e) => setAppreciationForm({
                      ...appreciationForm,
                      end_date: e.target.value
                    })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appréciation
                  </label>
                  <textarea
                    value={appreciationForm.content}
                    onChange={(e) => setAppreciationForm({
                      ...appreciationForm,
                      content: e.target.value
                    })}
                    className="input-field"
                    rows={4}
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAppreciationModal(false)}
                  className="btn btn-outline"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudents;